// useSocketListeners.js - FIXED VERSION
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { socket } from "../../../common/socket";
import {
  setTypingUser,
  updateChatListOrder,
  updateGroupChatLastMessage,
  addNewMessage,
  clearUnread,
  clearPendingForChat
} from "../../../slices/chat/reducer";
import { fetchAllGroupChats } from "../../../slices/chat/thunk";

export const useSocketListeners = ({
  selectedChatId,
  currentChat,
  currentUserId,
  handleNewMessage,
  handleMessageEdited,
  handleMessagesDeleted,
  isAtBottomRef,
  chatEndRef,
  setShowScrollButton,
  setNewMessageCount
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    console.log("🔔 [SOCKET] Setting up socket listeners for chat:", {
      selectedChatId: selectedChatId,
      currentUserId: currentUserId,
      socketConnected: socket.connected,
      timestamp: new Date().toISOString()
    });

    // ✅ FIXED: Handle new messages with proper imports
    // Alternative version without clearUnread
    const handleNewMessageWrapper = (message) => {
      console.log("💬 [SOCKET] NEW MESSAGE RECEIVED:", {
        messageId: message._id,
        chatId: message.chatId,
        senderId: message.sender?._id,
        content: message.content,
        isForCurrentChat: String(message.chatId) === String(selectedChatId),
        timestamp: new Date().toISOString()
      });

      const messageChatId = message.chatId || message.chatRequestId;
      if (!messageChatId) {
        console.log("❌ [SOCKET] No chat ID in message");
        return;
      }

      const isForCurrentChat = String(messageChatId) === String(selectedChatId);
      const isFromCurrentUser = String(message.sender?._id) === String(currentUserId);

      // ✅ FIXED: Always add message to Redux store
      dispatch(addNewMessage(message));

      if (isForCurrentChat) {
        console.log("📨 [SOCKET] Message for current chat, processing...");

        // If we have a custom handler, use it
        if (handleNewMessage) {
          handleNewMessage(message);
        }

        // Scroll to bottom if at bottom
        if (isAtBottomRef?.current) {
          setTimeout(() => {
            if (chatEndRef?.current) {
              chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }, 100);
        } else {
          // Show scroll button if not at bottom
          setShowScrollButton?.(true);
          setNewMessageCount?.(prev => prev + 1);
        }

        // Clear unread badge inside Redux when message belongs to currently open chat
        dispatch(clearUnread(messageChatId));

        try {
          const authUser = JSON.parse(localStorage.getItem("authUser"));
          const myId = authUser?.user?._id || authUser?._id;
          if (myId) {
            dispatch(clearPendingForChat({ chatId: messageChatId, userId: myId }));
          }
        } catch (error) {
          console.error("Error clearing pending:", error);
        }
      } else {
        console.log("📨 [SOCKET] Message for different chat, updating list...");
      }

      // ✅ FIXED: Always update chat list and last message
      dispatch(updateChatListOrder(messageChatId));

      if (message.content || message.mediaUrl) {
        dispatch(updateGroupChatLastMessage({
          chatId: messageChatId,
          lastMessage: message,
          lastMessageTimestamp: message.createdAt || new Date().toISOString()
        }));
      }

      if (!isForCurrentChat && !isFromCurrentUser) {
        console.log("🔁 [SOCKET] Refreshing group list from backend after new message:", messageChatId);
        dispatch(fetchAllGroupChats({ page: 1, limit: 50 }));
      }
    };

    // ✅ Handle chat list updates
    const handleChatListUpdate = (data) => {
      console.log("📡 [SOCKET] CHAT LIST UPDATE:", {
        action: data.action,
        chatId: data.chatId,
        hasLastMessage: !!data.lastMessage,
        timestamp: new Date().toISOString()
      });

      if (data.action === 'newMessage' || data.action === 'messageSent') {
        const chatId = data.chatId || data.lastMessage?.chatRequestId;
        if (!chatId) return;

        console.log("🔄 [SOCKET] Updating chat list for new message");

        // Update chat list order
        dispatch(updateChatListOrder(chatId));

        // Update last message
        if (data.lastMessage) {
          dispatch(updateGroupChatLastMessage({
            chatId,
            lastMessage: data.lastMessage,
            lastMessageTimestamp: data.lastMessage.createdAt || new Date().toISOString()
          }));
        }

        // Do not increment unread here; the real-time newMessage handler already does that.
      }
    };

    // ✅ Enhanced typing event handler
    const handleUserTyping = (data) => {
      const { chatId, userId, isTyping, user } = data;

      console.log(`⌨️ [SOCKET] TYPING EVENT RECEIVED:`, {
        chatId: chatId,
        userId: userId,
        isTyping: isTyping,
        forCurrentChat: String(chatId) === String(selectedChatId),
        timestamp: new Date().toISOString()
      });

      // Don't show typing indicator for self
      try {
        const authUser = JSON.parse(localStorage.getItem("authUser"));
        const myId = authUser?.user?._id || authUser?._id;

        if (myId && String(userId) === String(myId)) {
          console.log("👤 [SOCKET] Ignoring own typing event");
          return;
        }
      } catch (error) {
        console.error("❌ [SOCKET] Error checking user ID:", error);
      }

      if (chatId && String(chatId) === String(selectedChatId)) {
        console.log("🎯 [SOCKET] Applying typing indicator for current chat");
        dispatch(setTypingUser({
          chatId,
          userId,
          isTyping,
          user: user || { _id: userId }
        }));
      }
    };

    // ✅ Handle connection events
    const handleConnected = (data) => {
      console.log("✅ [SOCKET] SOCKET CONNECTED SUCCESSFULLY:", {
        socketId: data.socketId,
        userId: data.userId,
        timestamp: new Date().toISOString()
      });

      // Re-join chat room if we have a selected chat
      if (selectedChatId) {
        socket.emit("joinChat", { chatId: selectedChatId });
      }
    };

    const handleError = (error) => {
      console.error("❌ [SOCKET] SOCKET ERROR:", error);
    };

    // ✅ Handle room joined confirmation
    const handleRoomJoined = (data) => {
      console.log("🏠 [SOCKET] ROOM JOINED CONFIRMATION:", {
        chatId: data.chatId,
        room: data.room,
        success: data.success,
        timestamp: new Date().toISOString()
      });
    };

    // ✅ Register all socket events
    console.log("📝 [SOCKET] Registering socket event listeners");

    socket.on("connected", handleConnected);
    socket.on("newMessage", handleNewMessageWrapper);
    socket.on("userTyping", handleUserTyping);
    socket.on("chatList:update", handleChatListUpdate);
    socket.on("error", handleError);
    socket.on("messageEdited", handleMessageEdited);
    socket.on("messagesDeleted", handleMessagesDeleted);
    socket.on("roomJoined", handleRoomJoined);

    console.log("✅ [SOCKET] Socket listeners registered successfully");

    return () => {
      console.log("🧹 [SOCKET] Cleaning up socket listeners");
      socket.off("connected", handleConnected);
      socket.off("newMessage", handleNewMessageWrapper);
      socket.off("userTyping", handleUserTyping);
      socket.off("chatList:update", handleChatListUpdate);
      socket.off("error", handleError);
      socket.off("messageEdited", handleMessageEdited);
      socket.off("messagesDeleted", handleMessagesDeleted);
      socket.off("roomJoined", handleRoomJoined);
    };
  }, [
    selectedChatId,
    currentUserId,
    handleNewMessage,
    handleMessageEdited,
    handleMessagesDeleted,
    dispatch,
    isAtBottomRef,
    chatEndRef,
    setShowScrollButton,
    setNewMessageCount
  ]);
};